import { clamp, damp } from "./utils.js";

const DEFAULT_CAMERA_DISTANCE = 8.4;

/**
 * Converts semantic hand frames into smooth, frame-rate independent rendering
 * intent. Gesture recognition and Three.js remain completely decoupled.
 */
export class InteractionController {
  constructor({
    defaultCameraDistance = DEFAULT_CAMERA_DISTANCE,
    minimumCameraDistance = 5.2,
    maximumCameraDistance = 11.2,
    palmPitchSensitivity = 2.6,
    palmYawSensitivity = 3.2,
    maximumPitch = 1.4,
  } = {}) {
    this.defaultCameraDistance = defaultCameraDistance;
    this.minimumCameraDistance = minimumCameraDistance;
    this.maximumCameraDistance = maximumCameraDistance;
    this.palmPitchSensitivity = palmPitchSensitivity;
    this.palmYawSensitivity = palmYawSensitivity;
    this.rotationSensitivity = 1;
    this.maximumPitch = maximumPitch;
    this.reset();
  }

  setRotationSensitivity(value) {
    this.rotationSensitivity = clamp(value, 0.5, 2);
    return this.rotationSensitivity;
  }

  reset() {
    this.mode = "idle";
    this.features = createEmptyFeatures();
    this.handDetected = false;
    this.missingTime = 0;
    this.grabFrames = 0;
    this.releaseFrames = 0;
    this.palmMoveFrames = 0;
    this.palmStillFrames = 0;
    this.pendingRelease = 0;
    this.expansion = 0.5;
    this.targetExpansion = 0.5;
    this.attraction = 0;
    this.targetAttraction = 0;
    this.attractor = { x: 0, y: 0, z: 0 };
    this.targetAttractor = { x: 0, y: 0, z: 0 };
    this.releaseImpulse = 0;
    this.rotation = { x: 0, y: 0 };
    this.angularVelocity = { x: 0, y: 0 };
    this.targetAngularVelocity = { x: 0, y: 0 };
    this.palmVelocity = { x: 0, y: 0 };
    this.previousPalmSample = null;
    this.neutralPalmSize = null;
    this.cameraDistance = this.defaultCameraDistance;
    this.targetCameraDistance = this.defaultCameraDistance;
  }

  handleGestureFrame(frame) {
    if (!frame?.detected) {
      this.handleMissingHand();
      return;
    }

    this.handDetected = true;
    this.missingTime = 0;
    this.features = classifyHandFrame(frame);
    this.updatePalmVelocity(frame);
    const palmMoving = Math.hypot(
      this.palmVelocity.x,
      this.palmVelocity.y,
    ) > 0.11;
    this.updateMode(this.features, palmMoving);

    this.targetExpansion =
      this.mode === "grabbed" ? 0.03 : this.features.expansion;
    this.targetAttraction = this.mode === "grabbed" ? 1 : 0;
    this.targetAttractor.x = (frame.palm.x - 0.5) * 5.4;
    this.targetAttractor.y = (0.5 - frame.palm.y) * 4.1;
    this.targetAttractor.z = (frame.palm.depth - 0.5) * 1.35;

    if (this.mode === "palming") {
      this.targetAngularVelocity.x = clamp(
        applyDeadZone(this.palmVelocity.y, 0.025) *
          this.palmPitchSensitivity *
          this.rotationSensitivity,
        -3.8,
        3.8,
      );
      this.targetAngularVelocity.y = clamp(
        applyDeadZone(this.palmVelocity.x, 0.025) *
          this.palmYawSensitivity *
          this.rotationSensitivity,
        -4.5,
        4.5,
      );
    } else {
      this.targetAngularVelocity.x = 0;
      this.targetAngularVelocity.y = 0;
    }

    this.updateDollyTarget(frame);
  }

  handleMissingHand() {
    this.handDetected = false;
    this.mode = "idle";
    this.features = createEmptyFeatures();
    this.grabFrames = 0;
    this.releaseFrames = 0;
    this.palmMoveFrames = 0;
    this.palmStillFrames = 0;
    this.targetExpansion = 0.5;
    this.targetAttraction = 0;
    this.targetAngularVelocity.x = 0;
    this.targetAngularVelocity.y = 0;
    this.previousPalmSample = null;
  }

  updatePalmVelocity(frame) {
    const palm = frame.palm;
    if (
      this.previousPalmSample &&
      frame.timestamp > this.previousPalmSample.timestamp
    ) {
      const delta = clamp(
        (frame.timestamp - this.previousPalmSample.timestamp) / 1000,
        1 / 120,
        0.2,
      );
      const measuredX = (palm.x - this.previousPalmSample.x) / delta;
      const measuredY = (palm.y - this.previousPalmSample.y) / delta;
      this.palmVelocity.x =
        this.palmVelocity.x * 0.35 + measuredX * 0.65;
      this.palmVelocity.y =
        this.palmVelocity.y * 0.35 + measuredY * 0.65;
    } else {
      this.palmVelocity.x = Number.isFinite(palm.velocityX)
        ? palm.velocityX
        : 0;
      this.palmVelocity.y = Number.isFinite(palm.velocityY)
        ? palm.velocityY
        : 0;
    }

    this.previousPalmSample = {
      x: palm.x,
      y: palm.y,
      timestamp: frame.timestamp,
    };
  }

  updateMode(features, palmMoving) {
    this.grabFrames = features.grabCandidate ? this.grabFrames + 1 : 0;
    this.palmMoveFrames = palmMoving ? this.palmMoveFrames + 1 : 0;

    if (this.mode === "grabbed") {
      this.releaseFrames = features.grabCandidate
        ? 0
        : this.releaseFrames + 1;

      if (this.releaseFrames >= 2) {
        if (features.openness > 0.82) {
          this.pendingRelease = 1;
        }
        this.mode = this.palmMoveFrames >= 2
          ? "palming"
          : features.openCandidate
            ? "open"
            : "neutral";
        this.releaseFrames = 0;
      }
      return;
    }

    if (this.grabFrames >= 2) {
      this.mode = "grabbed";
      this.palmMoveFrames = 0;
      this.palmStillFrames = 0;
      return;
    }

    if (this.mode === "palming") {
      this.palmStillFrames = palmMoving ? 0 : this.palmStillFrames + 1;
      if (this.palmStillFrames < 3) {
        return;
      }
      this.palmStillFrames = 0;
    }
    if (this.palmMoveFrames >= 2) {
      this.mode = "palming";
      return;
    }

    this.mode = features.openCandidate ? "open" : "neutral";
  }

  updateDollyTarget(frame) {
    const palmSize = frame.palm.size;
    if (!Number.isFinite(palmSize) || palmSize <= 0) {
      return;
    }

    if (this.neutralPalmSize === null) {
      this.neutralPalmSize = palmSize;
    }

    const ratio = palmSize / Math.max(this.neutralPalmSize, 0.001);
    if (
      (this.mode === "neutral" || this.mode === "open") &&
      Math.abs(ratio - 1) < 0.07
    ) {
      this.neutralPalmSize =
        this.neutralPalmSize * 0.995 + palmSize * 0.005;
    }

    this.targetCameraDistance = clamp(
      this.defaultCameraDistance - (ratio - 1) * 4.8,
      this.minimumCameraDistance,
      this.maximumCameraDistance,
    );
  }

  update(delta) {
    const safeDelta = clamp(delta, 0, 0.05);

    if (!this.handDetected) {
      this.missingTime += safeDelta;
      if (this.missingTime > 0.75) {
        this.targetCameraDistance = this.defaultCameraDistance;
      }
    }

    this.expansion = damp(
      this.expansion,
      this.targetExpansion,
      8.5,
      safeDelta,
    );
    this.attraction = damp(
      this.attraction,
      this.targetAttraction,
      this.mode === "grabbed" ? 11 : 6.5,
      safeDelta,
    );
    this.attractor.x = damp(
      this.attractor.x,
      this.targetAttractor.x,
      12,
      safeDelta,
    );
    this.attractor.y = damp(
      this.attractor.y,
      this.targetAttractor.y,
      12,
      safeDelta,
    );
    this.attractor.z = damp(
      this.attractor.z,
      this.targetAttractor.z,
      10,
      safeDelta,
    );

    this.releaseImpulse = Math.max(
      this.pendingRelease,
      this.releaseImpulse * Math.exp(-5.2 * safeDelta),
    );
    this.pendingRelease = 0;

    const rotationResponse = this.mode === "palming" ? 11 : 1.7;
    this.angularVelocity.x = damp(
      this.angularVelocity.x,
      this.targetAngularVelocity.x,
      rotationResponse,
      safeDelta,
    );
    this.angularVelocity.y = damp(
      this.angularVelocity.y,
      this.targetAngularVelocity.y,
      rotationResponse,
      safeDelta,
    );
    this.rotation.x = clamp(
      this.rotation.x + this.angularVelocity.x * safeDelta,
      -this.maximumPitch,
      this.maximumPitch,
    );
    this.rotation.y += this.angularVelocity.y * safeDelta;

    if (
      (this.rotation.x <= -this.maximumPitch && this.angularVelocity.x < 0) ||
      (this.rotation.x >= this.maximumPitch && this.angularVelocity.x > 0)
    ) {
      this.angularVelocity.x = 0;
    }

    this.cameraDistance = damp(
      this.cameraDistance,
      this.targetCameraDistance,
      4.2,
      safeDelta,
    );

    return this.getSnapshot();
  }

  getSnapshot() {
    return {
      mode: this.mode,
      handDetected: this.handDetected,
      features: { ...this.features },
      particles: {
        expansion: this.expansion,
        attraction: this.attraction,
        attractor: { ...this.attractor },
        releaseImpulse: this.releaseImpulse,
      },
      rotation: {
        x: this.rotation.x,
        y: this.rotation.y,
        velocityX: this.angularVelocity.x,
        velocityY: this.angularVelocity.y,
      },
      camera: {
        distance: this.cameraDistance,
        neutralPalmSize: this.neutralPalmSize,
      },
    };
  }
}

/**
 * Stateless hand-pose features. Hysteresis is deliberately handled by the
 * controller so one noisy MediaPipe frame cannot switch interaction modes.
 */
export function classifyHandFrame(frame) {
  if (!frame?.detected || frame.landmarks?.length !== 21) {
    return createEmptyFeatures();
  }

  const palm = frame.palm;
  const landmarks = frame.landmarks;
  const extensionRatios = {
    thumb: extensionRatio(landmarks[4], landmarks[3], palm),
    index: extensionRatio(landmarks[8], landmarks[6], palm),
    middle: extensionRatio(landmarks[12], landmarks[10], palm),
    ring: extensionRatio(landmarks[16], landmarks[14], palm),
    pinky: extensionRatio(landmarks[20], landmarks[18], palm),
  };
  const extended = {
    thumb: extensionRatios.thumb > 1.14,
    index: extensionRatios.index > 1.27,
    middle: extensionRatios.middle > 1.27,
    ring: extensionRatios.ring > 1.25,
    pinky: extensionRatios.pinky > 1.22,
  };
  const extendedCount = Object.values(extended).filter(Boolean).length;
  const openness = frame.fingerDistances?.meanFromPalm ?? 0;
  const pinch = 1 - smoothstep(
    0.24,
    0.68,
    frame.fingerDistances?.thumbIndex ?? 1,
  );
  const expansion = smoothstep(0.62, 1.42, openness);
  const grabScore =
    (1 - smoothstep(0.72, 1.18, openness)) * 0.67 +
    (1 - extendedCount / 5) * 0.33;
  const grabCandidate =
    grabScore > 0.64 && extendedCount <= 1 && !extended.index;
  const openCandidate = extendedCount >= 4 || openness > 1.24;

  return {
    openness,
    expansion,
    pinch,
    grabScore,
    grabCandidate,
    openCandidate,
    extended,
    extendedCount,
  };
}

function createEmptyFeatures() {
  return {
    openness: 0,
    expansion: 0.5,
    pinch: 0,
    grabScore: 0,
    grabCandidate: false,
    openCandidate: false,
    extended: {
      thumb: false,
      index: false,
      middle: false,
      ring: false,
      pinky: false,
    },
    extendedCount: 0,
  };
}

function extensionRatio(tip, joint, palm) {
  return distance2D(tip, palm) / Math.max(distance2D(joint, palm), 0.001);
}

function distance2D(first, second) {
  return Math.hypot(first.x - second.x, first.y - second.y);
}

function smoothstep(minimum, maximum, value) {
  const normalized = clamp(
    (value - minimum) / (maximum - minimum),
    0,
    1,
  );
  return normalized * normalized * (3 - 2 * normalized);
}

function applyDeadZone(value, threshold) {
  const magnitude = Math.abs(value);
  if (magnitude <= threshold) {
    return 0;
  }
  return Math.sign(value) * (magnitude - threshold);
}
